import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import FileUploader from './FileUploader'
import { UPLOAD_STAGES } from 'components/utility/sandbox_upload/constants'
import { useStoreActions } from './uploadStore'
import { useInitUploader, useUploader } from 'components/utility/sandbox_upload/hooks'

/**
 * @typedef {Object} FileUploadPayload
 * @property {string} fileUUID - Unique identifier for the file
 * @property {Object} [file] - File metadata (only present in initial upload)
 * @property {string} file.name - Name of the file
 * @property {number} file.size - Size of the file in bytes
 * @property {string} file.type - MIME type of the file
 * @property {number} [progress] - Upload progress percentage (0-100)
 */

/**
 * @typedef {Object} FileErrorPayload
 * @property {string} fileUUID - Unique identifier for the file
 * @property {Object} errors - Error information
 * @property {string} errors.code - Error code identifier
 * @property {string} errors.message - Human-readable error message
 * @property {*} errors.details - Additional error context
 * @property {boolean} errors.retryable - Indicates if the upload can be retried
 */

/**
 * @typedef {Object} UploaderIdentifierPayload
 * @property {string} uploaderUUID - Unique identifier for the uploader
 */

/**
 * @typedef {Object} FileIdentifierPayload
 * @property {string} fileUUID - Unique identifier for the file
 */

/**
 * @typedef {Object} FileProcessedPayload
 * @property {string} fileUUID - Unique identifier for the file
 * @property {object} metadata - Metadata of the file
 */

/**
 * @callback onInitUploader
 * Called when an uploader is initialized
 * @param {UploaderIdentifierPayload} payload - Uploader information
 * @returns {void}
 */

/**
 * @callback onFileUpload
 * Called when a file is initially added or when upload progress updates
 * @param {FileUploadPayload} payload - File upload information
 * @returns {void}
 */

/**
 * @callback onFileProcessed
 * Called when a file upload has completed processing successfully
 * @param {FileProcessedPayload} payload - Information about the processed file
 * @returns {void}
 */

/**
 * @callback onFileError
 * Called when an error occurs during file upload or processing
 * @param {FileErrorPayload} payload - Error information for the file
 * @returns {void}
 */

/**
 * @callback onFileRemoved
 * Called when a file is removed from the upload queue
 * @param {FileIdentifierPayload} payload - Information about the removed file
 * @returns {void}
 */

/**
 * @component
 * @param {Object} props
 * @param {string} [props.uploaderUUID] - Label text for the file upload component
 * @param {string} [props.label] - Label text for the file upload component
 * @param {boolean} [props.multiple] - Whether multiple file selection is allowed
 * @param {Array} [props.accepts] - File types to be accepted
 * @param {Object} [props.fileSizes] - File sizes (bytes) by types. Default key is "*"
 * @param {onFileUpload} [props.onFileUpload] - Callback for file upload events
 * @param {onFileProcessed} [props.onFileProcessed] - Callback for successful file processing
 * @param {onFileError} [props.onFileError] - Callback for file upload errors
 * @param {onFileRemoved} [props.onFileRemoved] - Callback for file removal events
 */

export default function SandboxUploadComponent(props) {
  const uploaderUUID = useRef(props.uploaderUUID || uuidv4()).current

  const { multiple, accepts, fileSizes } = useMemo(() => {
    const {
      multiple = true,
      accepts = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.png', '.jpeg', '.jpg'],
      fileSizes = { '*': 50 * 1024 * 1024 },
    } = props
    return { multiple, accepts, fileSizes }
  }, [props.multiple, props.fileSizes, props.accepts])

  // const existingFiles = {
  //   '1fc90521-32d6-41fb-8a89-4bd0bf4d5ebe': {
  //     file: {
  //       name: '3000.pdf',
  //       type: 'application/pdf',
  //       size: 1000,
  //     },
  //     metadata: {
  //       urls: {
  //         pdf: 'http://localhost/3000.pdf',
  //         original: 'http://localhost/3000.pdf',
  //       },
  //     },
  //   },
  // }

  useStoreActions((action) => {
    if (action.payload.uploaderUUID === uploaderUUID) {
      switch (action.type) {
        case 'INIT_UPLOADER': {
          props.onInitUploader?.({
            uploaderUUID: action.payload.uploaderUUID,
          })
          break
        }
        case 'ADD_FILE': {
          props.onFileUpload?.({
            fileUUID: action.payload.fileUUID,
            file: action.payload.file,
            progress: 0,
          })
          break
        }
        case 'UPDATE_PROGRESS': {
          props.onFileUpload?.({
            fileUUID: action.payload.fileUUID,
            progress: action.payload.progress,
          })
          break
        }
        case 'UPDATE_STATUS': {
          if (action.payload.stage === UPLOAD_STAGES.COMPLETED) {
            props.onFileProcessed?.({
              fileUUID: action.payload.fileUUID,
              metadata: action.payload.metadata,
            })
          }
          break
        }
        case 'SET_ERROR': {
          props.onFileError?.({
            fileUUID: action.payload.fileUUID,
            errors: {
              code: action.payload.code,
              message: action.payload.message,
              details: action.payload.details,
            },
          })
          break
        }
        case 'REMOVE_FILE': {
          props.onFileRemoved?.({
            fileUUID: action.payload.fileUUID,
          })
        }
      }
    }
  })

  // useInitUploader(uploaderUUID, { accepts, fileSizes, multiple, existingFiles: props.existingFiles || {}, conversionFlags: { convert_to_images: true } })
  useInitUploader(uploaderUUID, {
    accepts,
    fileSizes,
    multiple,
    existingFiles: props.existingFiles || {},
    conversionFlags: props.conversionFlags || { convert_to_images: false },
  })

  const { submitPassword, removeFile, addFiles, files, errors, component } = useUploader(uploaderUUID)

  if (!component) return null

  return (
    <div>
      <FileUploader
        label={props.label}
        submitPassword={submitPassword}
        removeFile={removeFile}
        addFiles={addFiles}
        files={files}
        errors={errors}
        multiple={component.multiple}
        accepts={component.accepts}
      />
    </div>
  )
}

SandboxUploadComponent.propTypes = {
  uploaderUUID: PropTypes.string,
  accepts: PropTypes.array,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  existingFiles: PropTypes.object,
  fileSizes: PropTypes.object,
  conversionFlags: PropTypes.shape({
    convert_to_images: PropTypes.bool,
    skip_convert_to_pdf: PropTypes.bool,
  }),
  onInitUploader: PropTypes.func,
  onFileUpload: PropTypes.func,
  onFileProcessed: PropTypes.func,
  onFileError: PropTypes.func,
  onFileRemoved: PropTypes.func,
}
